import { forwardRef, useState, useMemo } from 'react';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { CheckmarkSolidIcon, MinusIcon } from '../Icons';
import checkBoxDefaultStyle from './checkBoxDefaultStyle';
import { interstateInputEventBuilder } from '../InterstateEvents';
import { useInterstateTheme } from '../InterstateThemeProvider';
import { mergeSxStyle } from '../common/mergeSxStyle';
import { checkBoxOnlyStyle } from './checkBoxOnlyStyle';
import { clsx } from 'clsx';
import { Box } from '../Box';
import { idBuilder } from '../internal';
import { jsx as _jsx } from "react/jsx-runtime";
export const CheckBox = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    checked,
    checkedIcon,
    classes,
    className,
    icon,
    defaultChecked,
    disabledChecked,
    disabled,
    id,
    indeterminate,
    indeterminateIcon,
    label,
    name,
    onBlur,
    onChange,
    onChangeNative,
    value,
    sx,
    ...rest
  } = _ref;
  const [checkedState, setCheckedState] = useState(defaultChecked || false);
  const theme = useInterstateTheme();
  const handleOnBlur = event => {
    if (onBlur) {
      const checkedValue = {
        checked: event.target?.checked,
        checkboxValue: value
      };
      onBlur(interstateInputEventBuilder(name, checkedValue, undefined, event, typeof label === 'string' ? label : undefined, undefined));
    }
  };
  const handleOnChange = event => {
    setCheckedState(event.target?.checked);
    if (onChange) {
      const checkedValue = {
        checked: event.target?.checked,
        checkboxValue: value
      };
      onChange(interstateInputEventBuilder(name, checkedValue, undefined, event, typeof label === 'string' ? label : undefined, undefined));
    }
    if (onChangeNative) {
      onChangeNative(event);
    }
  };
  const effectiveDataTestId = rest['data-testid'] ? `${rest['data-testid']}` : 'default-checkbox';
  const mergedCheckBoxOnlyStyle = useMemo(() => mergeSxStyle(checkBoxOnlyStyle(theme), []), [theme]);
  const mergedCheckBoxStyle = useMemo(() => mergeSxStyle(checkBoxDefaultStyle(theme), []), [theme]);
  if (label) {
    return /*#__PURE__*/_jsx(Box, {
      className: 'ids-checkbox-root',
      id: idBuilder('ids-checkbox-root', id),
      "data-testid": idBuilder('ids-checkbox-root', rest['data-testid']),
      sx: sx,
      children: /*#__PURE__*/_jsx(FormControlLabel, {
        control: /*#__PURE__*/_jsx(Checkbox, {
          className: clsx([`interstate-checkbox`]),
          id: id,
          classes: classes,
          onBlur: handleOnBlur,
          onChange: handleOnChange,
          checkedIcon: /*#__PURE__*/_jsx(CheckmarkSolidIcon, {
            "data-testid": effectiveDataTestId
          }) || checkedIcon,
          indeterminateIcon: /*#__PURE__*/_jsx(MinusIcon, {
            "data-testid": effectiveDataTestId
          }) || indeterminateIcon,
          checked: disabledChecked || checked,
          defaultChecked: defaultChecked,
          icon: icon,
          "data-testid": effectiveDataTestId,
          disabled,
          indeterminate,
          value,
          disableRipple: true,
          disableFocusRipple: true,
          disableTouchRipple: true,
          inputProps: {
            'data-testid': `input-${effectiveDataTestId}`,
            'data-checked': typeof checked !== 'undefined' ? checked : checkedState,
            className: clsx([`interstate-checkbox-input`, className])
          }
        }),
        label: label,
        inputRef: ref,
        sx: mergedCheckBoxStyle
      })
    });
  } else {
    return /*#__PURE__*/_jsx(Checkbox, {
      sx: mergedCheckBoxOnlyStyle,
      className: `interstate-checkbox`,
      id: id,
      classes: classes,
      onBlur: handleOnBlur,
      onChange: handleOnChange,
      checkedIcon: /*#__PURE__*/_jsx(CheckmarkSolidIcon, {
        "data-testid": effectiveDataTestId
      }) || checkedIcon,
      indeterminateIcon: /*#__PURE__*/_jsx(MinusIcon, {
        "data-testid": effectiveDataTestId
      }) || indeterminateIcon,
      checked: disabledChecked || checked,
      defaultChecked: defaultChecked,
      icon: icon,
      "data-testid": effectiveDataTestId,
      disabled,
      indeterminate,
      value,
      disableRipple: true,
      disableFocusRipple: true,
      disableTouchRipple: true,
      inputProps: {
        'data-testid': `input-${effectiveDataTestId}`,
        'data-checked': typeof checked !== 'undefined' ? checked : checkedState,
        className: clsx([`interstate-checkbox-input`, className])
      }
    });
  }
});
CheckBox.displayName = 'CheckBox';